import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { WebsiteFilter } from '@/components/website/WebsiteFilter';
import { ContentCard } from '@/components/website/ContentCard';
import { ContentModal } from '@/components/website/ContentModal';
import { useWebsiteContent } from '@/hooks/useWebsiteContent';
import type { WebsiteName } from '@/types/website';

export default function CRMWebsiteContent() {
  const navigate = useNavigate();
  const [websiteFilter, setWebsiteFilter] = useState<WebsiteName | 'all'>('all');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const filter = websiteFilter === 'all' ? undefined : websiteFilter;
  const { content, createContent } = useWebsiteContent(filter);

  const filtered = content.filter(c => 
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    c.slug.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async (data: { title: string; slug: string; website: WebsiteName }) => {
    const result = await createContent.mutateAsync(data);
    if (result?.id) navigate(`/crm/website/content/${result.id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Website Content</h1>
        <div className="flex items-center gap-3">
          <WebsiteFilter value={websiteFilter} onChange={setWebsiteFilter} />
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Nieuwe pagina
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op titel of slug..."
          className="pl-9"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(c => (
          <ContentCard
            key={c.id}
            content={c}
            onClick={() => navigate(`/crm/website/content/${c.id}`)}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-muted-foreground col-span-full text-center py-8">
            Geen content gevonden
          </p>
        )}
      </div>

      <ContentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSave={handleCreate}
        defaultWebsite={websiteFilter === 'all' ? undefined : websiteFilter}
      />
    </div>
  );
}